import { BellRing, MailCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { brandConfig } from "../../../config/brand";
import { getAnalyticsSessionId } from "../../lib/analyticsClient";
import { Button } from "../ui/Button";
import { Section } from "../ui/Section";

async function readApiMessage(response: Response) {
  const data = await response.json().catch(() => null);
  if (data && typeof data === "object" && "message" in data) return String(data.message);
  const error = data && typeof data === "object" && "error" in data ? data.error : null;
  if (error && typeof error === "object" && "message" in error) return String(error.message);
  return response.ok ? "Η δήλωση υπενθύμισης καταχωρήθηκε." : "Δεν μπορέσαμε να καταχωρήσουμε την υπενθύμιση αυτή τη στιγμή.";
}

export function RetestNotificationSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const submitReminder = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch("/api/contact/progress-reminder", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, analyticsSessionId: getAnalyticsSessionId() })
      });
      const nextFeedback = await readApiMessage(response);

      if (!response.ok) {
        setStatus("error");
        setFeedback(nextFeedback);
        return;
      }

      setStatus("success");
      setFeedback(nextFeedback);
      setEmail("");
    } catch {
      setStatus("error");
      setFeedback("Δεν μπορέσαμε να καταχωρήσουμε την υπενθύμιση αυτή τη στιγμή.");
    }
  };

  return (
    <Section className="py-12 sm:py-16">
      <div className="overflow-hidden rounded-[1.75rem] border border-cyan-200/20 bg-gradient-to-br from-slate-900/95 via-blue-950/70 to-slate-950 shadow-[0_28px_85px_rgb(0_0_0_/_0.32),0_0_45px_rgb(34_211_238_/_0.1)]">
        <div className="grid gap-6 p-5 sm:p-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-200/10 text-cyan-200 ring-1 ring-cyan-200/25">
              <BellRing size={22} />
            </div>
            <h2 className="mt-5 text-3xl font-bold text-white sm:text-4xl">Υπενθύμιση προόδου</h2>
            <p className="mt-3 max-w-2xl text-slate-300">
              Άφησέ μας μήνυμα για να δηλώσεις ότι θέλεις υπενθύμιση προόδου και να ξαναδοκιμάσεις το TryBrainTest μετά από ένα εύλογο χρονικό διάστημα.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-400">
              Θα χρησιμοποιηθεί μόνο για υπενθυμίσεις προόδου, ενημερώσεις report και προαιρετικές προσφορές επιστροφής.
            </p>
          </div>

          <form
            className="rounded-3xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgb(255_255_255_/_0.05)] sm:p-5"
            onSubmit={submitReminder}
          >
            <label className="text-sm font-semibold text-slate-200" htmlFor="challenge-email">
              Το email σου
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                id="challenge-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={160}
                placeholder="Το email σου"
                className="min-h-12 flex-1 rounded-2xl border border-white/10 bg-slate-950/70 px-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-200/70"
              />
              <Button
                type="submit"
                disabled={status === "submitting"}
                className="px-5"
              >
                <MailCheck className="mr-2" size={18} />
                {status === "submitting" ? "Καταχώρηση..." : "Θέλω υπενθύμιση"}
              </Button>
            </div>
            {feedback ? (
              <p className={`mt-3 rounded-lg border p-3 text-xs leading-5 ${status === "success" ? "border-emerald-200/25 bg-emerald-200/10 text-emerald-100" : "border-rose-200/25 bg-rose-200/10 text-rose-100"}`}>
                {feedback}
              </p>
            ) : null}
            <p className="mt-3 text-xs leading-5 text-slate-400">
              Για υπενθύμιση προόδου μπορείς επίσης να επικοινωνήσεις στο {brandConfig.supportEmail}.
            </p>
          </form>
        </div>
      </div>
    </Section>
  );
}
